import type { UIMessage } from "ai";

type MessagePart = UIMessage["parts"][number];

function isCompactionTextPart(part: MessagePart): boolean {
  return (
    part.type === "text" &&
    (part.providerMetadata?.anthropic?.type === "compaction" ||
      part.providerMetadata?.openai?.type === "compaction")
  );
}

/**
 * Join display text parts before handing them to a Markdown parser.
 *
 * Providers may split a single Markdown document into several AI SDK text
 * parts (for example around grounded/source-backed spans). Rendering each
 * fragment independently breaks lists and turns punctuation-only fragments
 * into separate paragraphs. Citation metadata is transparent, and whitespace
 * fragments are retained because they carry Markdown structure. Rendered files,
 * tools, compaction, and consumed parts remain hard boundaries.
 */
export function coalesceRenderableTextParts(
  parts: MessagePart[],
  consumedIndices: ReadonlySet<number> = new Set()
): Map<number, string> {
  const grouped = new Map<number, string>();
  let groupStart: number | null = null;

  parts.forEach((part, index) => {
    // Citations may interrupt a sentence without introducing visible content.
    // Container-file sources are different: they render a download card.
    if (
      !consumedIndices.has(index) &&
      (part.type === "source-url" ||
        (part.type === "source-document" &&
          part.providerMetadata?.openai?.type !== "container_file_citation"))
    ) return;

    const isRenderableText =
      part.type === "text" &&
      !isCompactionTextPart(part) &&
      !consumedIndices.has(index);

    if (!isRenderableText) {
      groupStart = null;
      return;
    }

    if (groupStart === null) {
      groupStart = index;
      grouped.set(index, part.text);
      return;
    }

    grouped.set(groupStart, `${grouped.get(groupStart) ?? ""}${part.text}`);
  });

  return grouped;
}

const MARKDOWN_TABLE_DIVIDER =
  /^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/;
const GENERIC_STRUCTURED_RESULT_INTRO =
  /^\s*(?:here(?:['’]s| are| is)\b|below (?:is|are)\b|the following (?:is|are)\b)/i;

/**
 * Remove Markdown tables that duplicate an interactive structured-result card.
 * A short generic introduction immediately attached to the table is removed as
 * well, while recommendations or other useful prose remain visible.
 */
export function stripDuplicateStructuredResultTables(text: string): string {
  const lines = text.split("\n");
  let removedTable = false;

  for (let dividerIndex = 1; dividerIndex < lines.length; dividerIndex += 1) {
    if (!MARKDOWN_TABLE_DIVIDER.test(lines[dividerIndex])) continue;
    if (!lines[dividerIndex - 1]?.includes("|")) continue;

    let tableEnd = dividerIndex + 1;
    while (tableEnd < lines.length && lines[tableEnd]!.includes("|")) {
      tableEnd += 1;
    }

    let tableStart = dividerIndex - 1;
    let introIndex = tableStart - 1;
    while (introIndex >= 0 && !lines[introIndex]!.trim()) introIndex -= 1;
    if (introIndex >= 0 && GENERIC_STRUCTURED_RESULT_INTRO.test(lines[introIndex]!)) {
      tableStart = introIndex;
    }

    lines.splice(tableStart, tableEnd - tableStart);
    removedTable = true;
    dividerIndex = Math.max(0, tableStart - 1);
  }

  if (!removedTable) return text;
  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
